import { useState, useEffect } from "react";

// ─── Constants ──────────────────────────────────────────────────────────────────
const ALL_EQUIPMENT = ["Barbell", "Dumbbells", "Cable", "Machine", "Kettlebell", "Resistance Bands", "Pull-Up Bar", "Bench", "Bodyweight"];

const DEFAULT_HOME_GYM = ["Dumbbells", "Bodyweight", "Pull-Up Bar", "Resistance Bands"];

const LOCATIONS = [
  { id: "home", label: "Home Gym", icon: "🏠" },
  { id: "full", label: "Full Gym", icon: "🏋️" },
  { id: "away", label: "Away", icon: "✈️" },
];

const EXERCISES = [
  { id: 1,  name: "Barbell Bench Press",       muscle: "Chest",     equipment: "Barbell",         sets: 4, reps: "8-10",  description: "Compound chest, shoulder, and tricep press; retract shoulder blades and keep feet flat throughout." },
  { id: 2,  name: "Dumbbell Fly",               muscle: "Chest",     equipment: "Dumbbells",        sets: 3, reps: "12-15", description: "Chest isolation with a deep stretch; keep a slight bend in the elbows and control the descent." },
  { id: 3,  name: "Push-Up",                    muscle: "Chest",     equipment: "Bodyweight",       sets: 3, reps: "15-20", description: "Upper body press requiring no equipment; keep hips level and lower chest to just above the floor." },
  { id: 4,  name: "Incline Dumbbell Press",     muscle: "Chest",     equipment: "Dumbbells",        sets: 3, reps: "10-12", description: "Targets the upper chest; set the bench to 30–45° and press with palms facing forward." },
  { id: 5,  name: "Cable Crossover",            muscle: "Chest",     equipment: "Cable",            sets: 3, reps: "12-15", description: "Constant-tension chest fly; squeeze at the crossing point for peak contraction." },
  { id: 6,  name: "Deadlift",                   muscle: "Back",      equipment: "Barbell",          sets: 4, reps: "5-6",   description: "Full-body pull for hamstrings, glutes, and back; brace your core and keep the bar close to your legs." },
  { id: 7,  name: "Pull-Up",                    muscle: "Back",      equipment: "Pull-Up Bar",      sets: 4, reps: "8-12",  description: "Compound pull for back width; initiate by depressing the shoulder blades before bending the elbows." },
  { id: 8,  name: "Barbell Row",                muscle: "Back",      equipment: "Barbell",          sets: 4, reps: "8-10",  description: "Horizontal pull for mid-back thickness; hinge at the hips and drive elbows back past your torso." },
  { id: 9,  name: "Dumbbell Row",               muscle: "Back",      equipment: "Dumbbells",        sets: 3, reps: "10-12", description: "Single-arm horizontal pull; brace on a bench and pull the elbow back past your hip." },
  { id: 10, name: "Lat Pulldown",               muscle: "Back",      equipment: "Cable",            sets: 3, reps: "10-12", description: "Cable pull for back width; lean slightly back and pull the bar to your upper chest." },
  { id: 11, name: "Seated Cable Row",           muscle: "Back",      equipment: "Cable",            sets: 3, reps: "10-12", description: "Mid-back compound pull; sit tall and squeeze shoulder blades together at the end of each rep." },
  { id: 12, name: "Resistance Band Row",        muscle: "Back",      equipment: "Resistance Bands", sets: 3, reps: "15",    description: "Horizontal pull variation; anchor the band at chest height and drive elbows straight back." },
  { id: 13, name: "Barbell Squat",              muscle: "Legs",      equipment: "Barbell",          sets: 4, reps: "6-8",   description: "King of leg exercises for quads, hamstrings, and glutes; brace hard, chest up, squat to parallel or below." },
  { id: 14, name: "Romanian Deadlift",          muscle: "Legs",      equipment: "Barbell",          sets: 3, reps: "10-12", description: "Hamstring and glute hinge; maintain a slight knee bend, push hips back, and lower the bar along your legs." },
  { id: 15, name: "Leg Press",                  muscle: "Legs",      equipment: "Machine",          sets: 4, reps: "10-12", description: "Machine quad and glute press; keep lower back pressed into the pad and control the descent." },
  { id: 16, name: "Bulgarian Split Squat",      muscle: "Legs",      equipment: "Dumbbells",        sets: 3, reps: "10-12", description: "Single-leg quad and glute exercise; rear foot elevated, lower until front thigh is parallel." },
  { id: 17, name: "Goblet Squat",               muscle: "Legs",      equipment: "Dumbbells",        sets: 3, reps: "12-15", description: "Front-loaded squat great for technique; hold the dumbbell at chest height and sit back between your heels." },
  { id: 18, name: "Bodyweight Squat",           muscle: "Legs",      equipment: "Bodyweight",       sets: 3, reps: "20",    description: "Foundational lower body pattern; sit back and down, keep heels flat, drive through the whole foot." },
  { id: 19, name: "Walking Lunge",              muscle: "Legs",      equipment: "Bodyweight",       sets: 3, reps: "16",    description: "Alternating single-leg lunge for quads, glutes, and balance; keep trunk upright and front knee behind toes." },
  { id: 20, name: "Leg Curl",                   muscle: "Legs",      equipment: "Machine",          sets: 3, reps: "12-15", description: "Machine hamstring isolation; curl smoothly and avoid letting hips rise off the pad." },
  { id: 21, name: "Kettlebell Swing",           muscle: "Legs",      equipment: "Kettlebell",       sets: 4, reps: "15",    description: "Hip-hinge power builder for glutes and hamstrings; drive with the hips, not the arms." },
  { id: 22, name: "Calf Raise",                 muscle: "Legs",      equipment: "Bodyweight",       sets: 4, reps: "15-20", description: "Gastrocnemius isolation; rise on the ball of the foot, pause at the top, and lower fully." },
  { id: 23, name: "Overhead Press",             muscle: "Shoulders", equipment: "Barbell",          sets: 4, reps: "6-8",   description: "Compound vertical press for the whole shoulder; brace your core and press in a straight line overhead." },
  { id: 24, name: "Dumbbell Shoulder Press",    muscle: "Shoulders", equipment: "Dumbbells",        sets: 3, reps: "10-12", description: "Shoulder press with greater range; press from ear height to full extension without flaring elbows." },
  { id: 25, name: "Lateral Raise",              muscle: "Shoulders", equipment: "Dumbbells",        sets: 3, reps: "15",    description: "Side delt isolation; lead with the elbows, raise to shoulder height, and lower under control." },
  { id: 26, name: "Face Pull",                  muscle: "Shoulders", equipment: "Cable",            sets: 3, reps: "15",    description: "Rear delt and rotator cuff health; pull to face level with elbows high, using a rope attachment." },
  { id: 27, name: "Arnold Press",               muscle: "Shoulders", equipment: "Dumbbells",        sets: 3, reps: "10-12", description: "Rotating press hitting all three deltoid heads; start with palms facing you and rotate out as you press." },
  { id: 28, name: "Band Pull-Apart",            muscle: "Shoulders", equipment: "Resistance Bands", sets: 3, reps: "20",    description: "Rear delt and posture exercise; keep arms straight and pull the band apart at chest height." },
  { id: 29, name: "Barbell Curl",               muscle: "Arms",      equipment: "Barbell",          sets: 3, reps: "10-12", description: "Bicep mass builder; keep elbows pinned at your sides and avoid swinging the bar." },
  { id: 30, name: "Hammer Curl",                muscle: "Arms",      equipment: "Dumbbells",        sets: 3, reps: "12",    description: "Brachialis and forearm curl with a neutral grip; keep wrists straight and curl with control." },
  { id: 31, name: "Tricep Dip",                 muscle: "Arms",      equipment: "Bodyweight",       sets: 3, reps: "12-15", description: "Bodyweight tricep press; lean slightly forward to bias the triceps and lower until elbows are at 90°." },
  { id: 32, name: "Skull Crusher",              muscle: "Arms",      equipment: "Barbell",          sets: 3, reps: "10-12", description: "Tricep isolation; lower the bar toward your forehead with elbows pointing up, then press to lockout." },
  { id: 33, name: "Cable Tricep Pushdown",      muscle: "Arms",      equipment: "Cable",            sets: 3, reps: "12-15", description: "Tricep isolation with constant tension; keep elbows fixed at your sides and extend fully." },
  { id: 34, name: "Band Bicep Curl",            muscle: "Arms",      equipment: "Resistance Bands", sets: 3, reps: "15",    description: "Bicep curl with a band; anchor underfoot and curl with control, pausing at peak contraction." },
  { id: 35, name: "Plank",                      muscle: "Core",      equipment: "Bodyweight",       sets: 3, reps: "60s",   description: "Full core and shoulder stability hold; keep hips level, brace your abs, and breathe steadily." },
  { id: 36, name: "Hanging Leg Raise",          muscle: "Core",      equipment: "Pull-Up Bar",      sets: 3, reps: "12-15", description: "Lower ab and hip flexor exercise; hang from a bar and raise legs to 90° without swinging." },
  { id: 37, name: "Ab Wheel Rollout",           muscle: "Core",      equipment: "Bodyweight",       sets: 3, reps: "10-12", description: "Demanding anti-extension core drill; roll out slowly and pull back using abs, not momentum." },
  { id: 38, name: "Russian Twist",              muscle: "Core",      equipment: "Bodyweight",       sets: 3, reps: "20",    description: "Rotational core exercise; lift feet off the floor and rotate the torso side to side with control." },
  { id: 39, name: "Cable Crunch",               muscle: "Core",      equipment: "Cable",            sets: 3, reps: "15",    description: "Loaded ab flexion; kneel and crunch your ribs toward your hips, not your face toward your knees." },
  { id: 40, name: "Kettlebell Turkish Get-Up",  muscle: "Core",      equipment: "Kettlebell",       sets: 3, reps: "5",     description: "Full-body stability drill; move slowly from lying to standing with the bell locked overhead throughout." },
  { id: 41, name: "Dead Bug",                   muscle: "Core",      equipment: "Bodyweight",       sets: 3, reps: "10",    description: "Core stability drill; press lower back into the floor as you slowly extend opposite arm and leg." },
  { id: 42, name: "Bird Dog",                   muscle: "Core",      equipment: "Bodyweight",       sets: 3, reps: "10",    description: "Anti-rotation core and back exercise; from all fours, extend opposite arm and leg without arching the lower back." },
  { id: 43, name: "Side Plank",                 muscle: "Core",      equipment: "Bodyweight",       sets: 3, reps: "45s",   description: "Lateral core (oblique) stability hold; stack or stagger your feet and keep hips lifted throughout." },
  { id: 44, name: "Hollow Body Hold",           muscle: "Core",      equipment: "Bodyweight",       sets: 3, reps: "30s",   description: "Core compression hold; press lower back into the floor, tuck chin, and hold arms and legs off the ground." },
  { id: 45, name: "Pallof Press",               muscle: "Core",      equipment: "Cable",            sets: 3, reps: "12",    description: "Anti-rotation core exercise; press the cable handle straight out and resist the pull of the cable." },
  { id: 46, name: "Dragon Flag",                muscle: "Core",      equipment: "Bench",            sets: 3, reps: "6-8",   description: "Advanced full-body core exercise; keep body rigid from shoulders to toes as you lower and raise on a bench." },
  { id: 47, name: "Suitcase Carry",             muscle: "Core",      equipment: "Dumbbells",        sets: 3, reps: "30m",   description: "Unilateral loaded carry for obliques and grip; walk tall with one dumbbell, resist tilting to either side." },
  { id: 48, name: "Band Pallof Press",          muscle: "Core",      equipment: "Resistance Bands", sets: 3, reps: "12",    description: "Anti-rotation core with a band; anchor at chest height and press straight out, resisting rotation." },
  { id: 49, name: "Thoracic Rotation",          muscle: "Mobility",  equipment: "Bodyweight",       sets: 2, reps: "10",    description: "Upper back mobility; rotate through the thoracic spine rather than the lower back." },
  { id: 50, name: "Hip 90/90 Stretch",          muscle: "Mobility",  equipment: "Bodyweight",       sets: 2, reps: "60s",   description: "Hip external and internal rotation stretch; sit with both legs at 90° angles and hold for time each side." },
  { id: 51, name: "World's Greatest Stretch",   muscle: "Mobility",  equipment: "Bodyweight",       sets: 2, reps: "6",     description: "Full-body mobility drill combining hip flexor, thoracic, and hamstring stretch in one flowing movement." },
  { id: 52, name: "Cat-Cow",                    muscle: "Mobility",  equipment: "Bodyweight",       sets: 2, reps: "12",    description: "Spinal flexion and extension warm-up on all fours; breathe into each position and move with the breath." },
  { id: 53, name: "Ankle Circle",               muscle: "Mobility",  equipment: "Bodyweight",       sets: 2, reps: "15",    description: "Ankle joint mobilisation; draw slow circles in each direction to increase range of motion before squatting." },
  { id: 54, name: "Hip Circle",                 muscle: "Mobility",  equipment: "Bodyweight",       sets: 2, reps: "10",    description: "Hip joint mobilisation standing on one leg; draw large slow circles to loosen the hip socket." },
  { id: 55, name: "Couch Stretch",              muscle: "Mobility",  equipment: "Bodyweight",       sets: 2, reps: "60s",   description: "Hip flexor and quad stretch; place rear shin against a wall or bench and sit tall in a kneeling position." },
  { id: 56, name: "Pigeon Pose",                muscle: "Mobility",  equipment: "Bodyweight",       sets: 2, reps: "90s",   description: "Deep hip external rotation stretch; hold passively or add a forward fold for greater depth." },
  { id: 57, name: "Jefferson Curl",             muscle: "Mobility",  equipment: "Dumbbells",        sets: 3, reps: "8",     description: "Loaded spinal flexion for posterior chain mobility; use very light weight and roll down vertebra by vertebra." },
  { id: 58, name: "Deep Squat Hold",            muscle: "Mobility",  equipment: "Bodyweight",       sets: 3, reps: "60s",   description: "Ankle and hip mobility in the bottom squat position; hold a surface for balance and work to keep heels flat." },
  { id: 59, name: "Doorframe Chest Stretch",    muscle: "Mobility",  equipment: "Bodyweight",       sets: 2, reps: "45s",   description: "Pectoral and anterior shoulder stretch; place forearm on a doorframe and gently rotate away." },
  { id: 60, name: "Wall Slide",                 muscle: "Mobility",  equipment: "Bodyweight",       sets: 3, reps: "12",    description: "Shoulder mobility and posture drill; press forearms to a wall and slide overhead without losing contact." },
];

const EXERCISE_MAP = new Map(EXERCISES.map(e => [e.name, e]));

const MUSCLE_GROUPS = ["All", "Chest", "Back", "Legs", "Shoulders", "Arms", "Core", "Mobility"];
const MUSCLE_COLOR = { Chest: "#e74c3c", Back: "#3498db", Legs: "#2ecc71", Shoulders: "#f39c12", Arms: "#9b59b6", Core: "#1abc9c", Mobility: "#e67e22" };

// ─── Styles ─────────────────────────────────────────────────────────────────────
const css = `
@import url('https://fonts.googleapis.com/css2?family=Barlow+Condensed:wght@400;600;700;800;900&family=DM+Mono:wght@400;500&display=swap');
*, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }
:root {
  --bg: #0d0d0f; --surface: #141416; --surface2: #1c1c20;
  --border: #2a2a30; --accent: #c8f135; --accent2: #ff5c35;
  --text: #f0f0f0; --muted: #666;
  --font-display: 'Barlow Condensed', sans-serif;
  --font-mono: 'DM Mono', monospace;
}
body { background: var(--bg); color: var(--text); font-family: var(--font-display); }
.app { min-height: 100vh; display: flex; flex-direction: column; }

.header { border-bottom: 1px solid var(--border); padding: 0 20px; display: flex; align-items: center; justify-content: space-between; height: 56px; position: sticky; top: 0; background: var(--bg); z-index: 100; }
.logo { font-size: 22px; font-weight: 900; letter-spacing: 0.12em; text-transform: uppercase; color: var(--accent); }
.logo span { color: var(--muted); }
.location-badge { font-family: var(--font-mono); font-size: 12px; color: var(--muted); border: 1px solid var(--border); border-radius: 2px; padding: 4px 10px; }
.location-badge strong { color: var(--accent); }

.nav { display: flex; border-bottom: 1px solid var(--border); background: var(--surface); padding: 0 20px; overflow-x: auto; }
.nav-btn { font-family: var(--font-display); font-weight: 700; font-size: 15px; letter-spacing: 0.1em; text-transform: uppercase; padding: 14px 18px; border: none; background: transparent; color: var(--muted); cursor: pointer; border-bottom: 3px solid transparent; margin-bottom: -1px; transition: all 0.15s; white-space: nowrap; }
.nav-btn.active { color: var(--accent); border-bottom-color: var(--accent); }
.nav-btn:hover:not(.active) { color: var(--text); }

.main { flex: 1; padding: 24px 20px; max-width: 900px; width: 100%; margin: 0 auto; }
.section-label { font-size: 11px; font-weight: 600; letter-spacing: 0.2em; text-transform: uppercase; color: var(--muted); margin-bottom: 16px; font-family: var(--font-mono); }

.location-row { display: flex; gap: 8px; margin-bottom: 20px; }
.loc-btn { flex: 1; font-family: var(--font-display); font-weight: 700; font-size: 14px; letter-spacing: 0.06em; text-transform: uppercase; padding: 12px 8px; border: 1px solid var(--border); border-radius: 2px; background: var(--surface); color: var(--muted); cursor: pointer; transition: all 0.15s; text-align: center; }
.loc-btn .loc-icon { display: block; font-size: 20px; margin-bottom: 4px; }
.loc-btn.active { border-color: var(--accent); color: var(--accent); background: rgba(200,241,53,0.07); }
.loc-btn:hover:not(.active) { border-color: #3a3a42; color: var(--text); }

.filter-row { display: flex; gap: 8px; flex-wrap: wrap; margin-bottom: 20px; }
.filter-chip { font-family: var(--font-display); font-weight: 700; font-size: 13px; letter-spacing: 0.06em; text-transform: uppercase; padding: 6px 14px; border: 1px solid var(--border); border-radius: 2px; background: transparent; color: var(--muted); cursor: pointer; transition: all 0.15s; }
.filter-chip.active { border-color: var(--accent); color: var(--accent); background: rgba(200,241,53,0.07); }
.filter-chip:hover:not(.active) { border-color: #444; color: var(--text); }

.search-input { width: 100%; background: var(--surface2); border: 1px solid var(--border); color: var(--text); font-family: var(--font-mono); font-size: 14px; padding: 10px 16px; border-radius: 2px; margin-bottom: 20px; outline: none; transition: border-color 0.15s; }
.search-input:focus { border-color: var(--accent); }
.search-input::placeholder { color: var(--muted); }

.exercise-grid { display: flex; flex-direction: column; gap: 2px; }
.exercise-row { display: flex; align-items: center; padding: 12px 16px; background: var(--surface); border: 1px solid var(--border); border-radius: 2px; gap: 12px; transition: border-color 0.15s; }
.exercise-row:hover { border-color: #3a3a42; }
.muscle-dot { width: 8px; height: 8px; border-radius: 50%; flex-shrink: 0; }
.ex-name { font-weight: 700; font-size: 15px; letter-spacing: 0.04em; flex: 1; }
.ex-tag { font-family: var(--font-mono); font-size: 11px; color: var(--muted); background: var(--surface2); border: 1px solid var(--border); padding: 3px 8px; border-radius: 2px; }

.build-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 12px; margin-bottom: 20px; }
@media (max-width: 600px) { .build-grid { grid-template-columns: 1fr; } }
.field-label { font-size: 11px; font-weight: 600; letter-spacing: 0.15em; text-transform: uppercase; color: var(--muted); font-family: var(--font-mono); margin-bottom: 8px; display: block; }
.select-input { width: 100%; background: var(--surface2); border: 1px solid var(--border); color: var(--text); font-family: var(--font-display); font-weight: 700; font-size: 15px; padding: 10px 14px; border-radius: 2px; outline: none; cursor: pointer; appearance: none; }
.select-input:focus { border-color: var(--accent); }

.equip-summary { background: var(--surface); border: 1px solid var(--border); border-radius: 2px; padding: 12px 16px; margin-bottom: 20px; font-family: var(--font-mono); font-size: 12px; color: var(--muted); line-height: 1.8; }
.equip-summary strong { color: var(--text); font-size: 13px; display: block; margin-bottom: 6px; }

.build-btn { width: 100%; font-family: var(--font-display); font-weight: 900; font-size: 18px; letter-spacing: 0.15em; text-transform: uppercase; padding: 16px; background: var(--accent); color: #000; border: none; border-radius: 2px; cursor: pointer; margin-bottom: 24px; transition: opacity 0.15s; }
.build-btn:hover { opacity: 0.9; }
.build-btn:disabled { opacity: 0.4; cursor: not-allowed; }

.workout-card { background: var(--surface); border: 1px solid var(--border); border-radius: 2px; overflow: hidden; margin-bottom: 20px; }
.workout-card-header { padding: 14px 16px; border-bottom: 1px solid var(--border); display: flex; align-items: center; justify-content: space-between; background: var(--surface2); }
.workout-title { font-weight: 800; font-size: 18px; letter-spacing: 0.08em; text-transform: uppercase; }
.workout-meta { font-family: var(--font-mono); font-size: 12px; color: var(--muted); }
.workout-exercise { display: flex; align-items: center; padding: 12px 16px; border-bottom: 1px solid var(--border); gap: 12px; flex-wrap: wrap; }
.workout-exercise:last-child { border-bottom: none; }
.ex-num { font-family: var(--font-mono); font-size: 12px; color: var(--muted); width: 20px; flex-shrink: 0; }
.ex-sets { font-family: var(--font-mono); font-size: 13px; color: var(--accent); flex-shrink: 0; min-width: 80px; text-align: right; }
.swap-btn { font-family: var(--font-mono); font-size: 11px; padding: 4px 10px; background: transparent; border: 1px solid var(--border); color: var(--muted); border-radius: 2px; cursor: pointer; flex-shrink: 0; transition: all 0.15s; }
.swap-btn:hover { border-color: var(--accent2); color: var(--accent2); }
.notes-block { padding: 12px 16px; background: rgba(200,241,53,0.04); border-top: 1px solid var(--border); font-family: var(--font-mono); font-size: 12px; color: var(--muted); line-height: 1.6; }

.save-btn { width: 100%; font-family: var(--font-display); font-weight: 800; font-size: 15px; letter-spacing: 0.1em; text-transform: uppercase; padding: 13px; background: transparent; border: 1px solid var(--accent); color: var(--accent); border-radius: 2px; cursor: pointer; transition: all 0.15s; }
.save-btn:hover { background: rgba(200,241,53,0.08); }

.plan-empty { text-align: center; padding: 60px 20px; color: var(--muted); font-family: var(--font-mono); font-size: 14px; border: 1px dashed var(--border); border-radius: 2px; }
.plan-card { background: var(--surface); border: 1px solid var(--border); border-radius: 2px; margin-bottom: 12px; overflow: hidden; }
.plan-card-header { padding: 12px 16px; background: var(--surface2); border-bottom: 1px solid var(--border); display: flex; align-items: center; justify-content: space-between; }
.plan-card-title { font-weight: 800; font-size: 15px; letter-spacing: 0.06em; text-transform: uppercase; }
.plan-card-meta { font-family: var(--font-mono); font-size: 11px; color: var(--muted); }
.delete-btn { background: transparent; border: 1px solid var(--border); color: var(--muted); font-size: 12px; padding: 4px 10px; border-radius: 2px; cursor: pointer; font-family: var(--font-mono); transition: all 0.15s; }
.delete-btn:hover { border-color: var(--accent2); color: var(--accent2); }
.plan-exercises { padding: 12px 16px; }
.plan-ex-row { display: flex; align-items: center; gap: 10px; padding: 6px 0; border-bottom: 1px solid rgba(255,255,255,0.04); font-size: 14px; }
.plan-ex-row:last-child { border-bottom: none; }

.settings-section { margin-bottom: 32px; }
.settings-title { font-size: 18px; font-weight: 800; letter-spacing: 0.08em; text-transform: uppercase; margin-bottom: 4px; }
.settings-desc { font-family: var(--font-mono); font-size: 12px; color: var(--muted); margin-bottom: 16px; line-height: 1.6; }
.equip-grid { display: flex; flex-direction: column; gap: 2px; }
.equip-row { display: flex; align-items: center; gap: 12px; padding: 12px 16px; background: var(--surface); border: 1px solid var(--border); border-radius: 2px; cursor: pointer; transition: border-color 0.15s; }
.equip-row:hover { border-color: #3a3a42; }
.equip-row.checked { border-color: rgba(200,241,53,0.3); background: rgba(200,241,53,0.04); }
.equip-check { width: 18px; height: 18px; border: 1px solid var(--border); border-radius: 2px; display: flex; align-items: center; justify-content: center; flex-shrink: 0; transition: all 0.15s; }
.equip-row.checked .equip-check { background: var(--accent); border-color: var(--accent); }
.check-mark { color: #000; font-size: 12px; font-weight: 900; }
.equip-name { font-weight: 700; font-size: 15px; flex: 1; }
.locked-tag { font-family: var(--font-mono); font-size: 11px; color: var(--muted); background: var(--surface2); border: 1px solid var(--border); padding: 3px 8px; border-radius: 2px; }
.save-settings-btn { font-family: var(--font-display); font-weight: 800; font-size: 15px; letter-spacing: 0.1em; text-transform: uppercase; padding: 12px 28px; background: var(--accent); color: #000; border: none; border-radius: 2px; cursor: pointer; transition: opacity 0.15s; margin-top: 16px; }
.save-settings-btn:hover { opacity: 0.9; }
.saved-flash { font-family: var(--font-mono); font-size: 12px; color: var(--accent); margin-left: 12px; }

.overlay { position: fixed; inset: 0; background: rgba(0,0,0,0.8); z-index: 200; display: flex; align-items: center; justify-content: center; padding: 20px; }
.modal { background: var(--surface); border: 1px solid var(--border); border-radius: 4px; width: 100%; max-width: 480px; max-height: 70vh; display: flex; flex-direction: column; overflow: hidden; }
.modal-header { padding: 16px 20px; border-bottom: 1px solid var(--border); display: flex; align-items: center; justify-content: space-between; }
.modal-title { font-weight: 800; font-size: 16px; letter-spacing: 0.06em; text-transform: uppercase; }
.close-btn { background: transparent; border: none; color: var(--muted); font-size: 20px; cursor: pointer; }
.modal-list { overflow-y: auto; flex: 1; }
.modal-ex { display: flex; align-items: center; padding: 12px 20px; border-bottom: 1px solid var(--border); gap: 12px; cursor: pointer; transition: background 0.1s; }
.modal-ex:hover { background: var(--surface2); }

.loading-state { text-align: center; padding: 40px; font-family: var(--font-mono); color: var(--muted); font-size: 13px; }
.ex-desc { font-family: var(--font-mono); font-size: 11px; color: var(--muted); margin-top: 2px; line-height: 1.5; }
.refine-bar { display: flex; gap: 8px; align-items: center; margin-top: 12px; margin-bottom: 4px; }
.refine-btn { font-family: var(--font-display); font-weight: 800; font-size: 14px; letter-spacing: 0.08em; text-transform: uppercase; padding: 10px 20px; background: transparent; color: var(--accent); border: 1px solid var(--accent); border-radius: 2px; cursor: pointer; white-space: nowrap; transition: all 0.15s; }
.refine-btn:hover:not(:disabled) { background: rgba(200,241,53,0.08); }
.refine-btn:disabled { opacity: 0.4; cursor: default; }
.garmin-pill { display: inline-flex; align-items: center; gap: 8px; font-family: var(--font-mono); font-size: 12px; color: var(--accent); background: rgba(200,241,53,0.07); border: 1px solid rgba(200,241,53,0.25); border-radius: 2px; padding: 6px 12px; margin-bottom: 16px; }
.garmin-pill .gp-muted { color: var(--muted); }
.garmin-connect-form { display: flex; flex-direction: column; gap: 8px; margin-top: 4px; }
.disconnect-btn { font-family: var(--font-mono); font-size: 12px; padding: 8px 16px; background: transparent; border: 1px solid var(--border); color: var(--muted); border-radius: 2px; cursor: pointer; transition: all 0.15s; margin-top: 8px; }
.disconnect-btn:hover { border-color: var(--accent2); color: var(--accent2); }
.loading-dots::after { content: ''; animation: dots 1.2s steps(4, end) infinite; }
@keyframes dots { 0%,100%{content:''} 25%{content:'.'} 50%{content:'..'} 75%{content:'…'} }
.error-msg { background: rgba(255,92,53,0.1); border: 1px solid rgba(255,92,53,0.3); color: var(--accent2); padding: 12px 16px; border-radius: 2px; font-family: var(--font-mono); font-size: 13px; margin-bottom: 16px; }
`;

async function generateWorkout(params) {
  const res = await fetch("/api/generate-workout", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(params),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.error || "Request failed");
  }
  return res.json();
}

export default function WorkoutApp() {
  const [tab, setTab] = useState("build");

  // Settings
  const [homeGym, setHomeGym] = useState(() => {
    try { return JSON.parse(localStorage.getItem("homeGym") || JSON.stringify(DEFAULT_HOME_GYM)); } catch { return DEFAULT_HOME_GYM; }
  });
  const [settingsSaved, setSettingsSaved] = useState(false);

  const saveSettings = () => {
    localStorage.setItem("homeGym", JSON.stringify(homeGym));
    setSettingsSaved(true);
    setTimeout(() => setSettingsSaved(false), 2000);
  };

  const toggleEquip = (eq) => setHomeGym(prev => prev.includes(eq) ? prev.filter(e => e !== eq) : [...prev, eq]);

  // Garmin
  const [garminCreds, setGarminCreds] = useState(() => {
    try { return JSON.parse(localStorage.getItem("garmin_creds") || "null"); } catch { return null; }
  });
  const [garminData, setGarminData] = useState(null);
  const [garminSyncing, setGarminSyncing] = useState(false);
  const [garminConnectError, setGarminConnectError] = useState(null);
  const [garminEmail, setGarminEmail] = useState("");
  const [garminPassword, setGarminPassword] = useState("");

  const syncGarmin = async (creds) => {
    if (!creds) return;
    setGarminSyncing(true);
    try {
      const res = await fetch("/api/garmin-sync", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(creds),
      });
      const data = await res.json();
      if (data.connected) {
        setGarminData(data);
      } else {
        setGarminData(null);
        setGarminConnectError(data.error || "Could not connect to Garmin");
      }
    } catch {
      setGarminData(null);
    } finally {
      setGarminSyncing(false);
    }
  };

  const connectGarmin = async () => {
    setGarminConnectError(null);
    setGarminSyncing(true);
    try {
      const res = await fetch("/api/garmin-sync", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: garminEmail, password: garminPassword }),
      });
      const data = await res.json();
      if (data.connected) {
        const creds = { email: garminEmail, password: garminPassword };
        localStorage.setItem("garmin_creds", JSON.stringify(creds));
        setGarminCreds(creds);
        setGarminData(data);
        setGarminEmail("");
        setGarminPassword("");
      } else {
        setGarminConnectError(data.error || "Could not connect — check your credentials");
      }
    } catch {
      setGarminConnectError("Connection failed");
    } finally {
      setGarminSyncing(false);
    }
  };

  const disconnectGarmin = () => {
    localStorage.removeItem("garmin_creds");
    setGarminCreds(null);
    setGarminData(null);
    setGarminConnectError(null);
  };

  useEffect(() => {
    if (tab === "build" && garminCreds && !garminData && !garminSyncing) {
      syncGarmin(garminCreds);
    }
  }, [tab]); // intentionally omits garminData/garminSyncing to run only on tab change

  // Location & equipment
  const [location, setLocation] = useState("home");
  const availableEquipment = location === "full" ? ALL_EQUIPMENT : location === "away" ? ["Bodyweight"] : homeGym;
  const availableExercises = EXERCISES.filter(e => availableEquipment.includes(e.equipment));

  // Build
  const [duration, setDuration] = useState("30");
  const [focus, setFocus] = useState("AI Decides");
  const [intensity, setIntensity] = useState("AI Decides");
  const [freeText, setFreeText] = useState("");
  const [generating, setGenerating] = useState(false);
  const [error, setError] = useState(null);
  const [generatedWorkout, setGeneratedWorkout] = useState(null);
  const [swapIndex, setSwapIndex] = useState(null);
  const [refinementText, setRefinementText] = useState("");
  const [refining, setRefining] = useState(false);

  const handleGenerate = async () => {
    setGenerating(true); setError(null); setGeneratedWorkout(null); setRefinementText("");
    try {
      const w = await generateWorkout({ duration, focus, intensity, availableEquipment, exercises: availableExercises, freeText, garminData });
      setGeneratedWorkout(w);
    } catch (e) {
      setError(e.message || "Failed to generate. Check API connection.");
    } finally { setGenerating(false); }
  };

  const handleRefine = async () => {
    if (!refinementText.trim() || !generatedWorkout) return;
    setRefining(true); setError(null);
    try {
      const w = await generateWorkout({ duration, availableEquipment, exercises: availableExercises, garminData, refinement: refinementText, currentWorkout: generatedWorkout });
      setGeneratedWorkout(w);
      setRefinementText("");
    } catch (e) {
      setError(e.message || "Refinement failed.");
    } finally { setRefining(false); }
  };

  const swapCandidates = swapIndex !== null && generatedWorkout ? (() => {
    const cur = generatedWorkout.exercises[swapIndex];
    const match = EXERCISES.find(e => e.name === cur.name);
    return EXERCISES.filter(e => e.muscle === match?.muscle && availableEquipment.includes(e.equipment) && e.name !== cur.name && !generatedWorkout.exercises.some((ex, i) => i !== swapIndex && ex.name === e.name));
  })() : [];

  const handleSwap = (ex) => {
    const updated = [...generatedWorkout.exercises];
    updated[swapIndex] = { name: ex.name, sets: ex.sets, reps: ex.reps, rest: "60s" };
    setGeneratedWorkout({ ...generatedWorkout, exercises: updated });
    setSwapIndex(null);
  };

  // Plans
  const [plans, setPlans] = useState(() => {
    try { return JSON.parse(localStorage.getItem("workout_plans") || "[]"); } catch { return []; }
  });

  const handleSave = () => {
    const newPlan = {
      id: Date.now(), ...generatedWorkout,
      savedAt: new Date().toLocaleDateString("en-AU", { day: "numeric", month: "short" }),
      meta: `${duration}min · ${focus} · ${intensity} · ${LOCATIONS.find(l => l.id === location)?.label}`,
    };
    const updated = [newPlan, ...plans];
    localStorage.setItem("workout_plans", JSON.stringify(updated));
    setPlans(updated); setGeneratedWorkout(null); setTab("plan");
  };

  const handleDelete = (id) => {
    const updated = plans.filter(p => p.id !== id);
    localStorage.setItem("workout_plans", JSON.stringify(updated));
    setPlans(updated);
  };

  // Library
  const [muscleFilter, setMuscleFilter] = useState("All");
  const [search, setSearch] = useState("");
  const [showAll, setShowAll] = useState(true);

  const locLabel = LOCATIONS.find(l => l.id === location);

  return (
    <>
      <style>{css}</style>
      <div className="app">
        <header className="header">
          <div className="logo">LIFT<span>.FIT</span></div>
          <div className="location-badge">{locLabel?.icon} <strong>{locLabel?.label}</strong></div>
        </header>

        <nav className="nav">
          {[["build","Build"],["plan","My Plan"],["library","Library"],["settings","Settings"]].map(([id, label]) => (
            <button key={id} className={`nav-btn${tab === id ? " active" : ""}`} onClick={() => setTab(id)}>{label}</button>
          ))}
        </nav>

        <main className="main">

          {tab === "build" && <>
            <p className="section-label">Location</p>
            <div className="location-row">
              {LOCATIONS.map(loc => (
                <button key={loc.id} className={`loc-btn${location === loc.id ? " active" : ""}`} onClick={() => { setLocation(loc.id); setGeneratedWorkout(null); setFreeText(""); }}>
                  <span className="loc-icon">{loc.icon}</span>{loc.label}
                </button>
              ))}
            </div>

            <div className="equip-summary">
              <strong>Available equipment</strong>
              {availableEquipment.join(" · ")}
              {location === "home" && <span style={{ display: "block", marginTop: 4, color: "var(--accent)", fontSize: 11 }}>Configure in Settings →</span>}
            </div>

            <div style={{ marginBottom: 20 }}>
              <label className="field-label">Tell Claude what you want</label>
              <textarea
                className="search-input"
                style={{ resize: "vertical", minHeight: 80, lineHeight: 1.5 }}
                placeholder={"e.g. core and mobility, nothing too heavy\nor: I did legs yesterday — upper body only\nor: feeling beat up, keep it light and short"}
                value={freeText}
                onChange={e => setFreeText(e.target.value)}
              />
            </div>

            <p className="section-label">Defaults <span style={{ fontWeight: 400, textTransform: "none", letterSpacing: 0, color: "var(--muted)" }}>— overridden by your request above</span></p>
            <div className="build-grid">
              <div>
                <label className="field-label">Duration</label>
                <select className="select-input" value={duration} onChange={e => setDuration(e.target.value)}>
                  {["20","30","45","60"].map(d => <option key={d} value={d}>{d} minutes</option>)}
                </select>
              </div>
              <div>
                <label className="field-label">Focus</label>
                <select className="select-input" value={focus} onChange={e => setFocus(e.target.value)}>
                  {["AI Decides","Full Body","Chest","Back","Legs","Shoulders","Arms","Core","Upper Body","Lower Body","Mobility","Recovery"].map(f => <option key={f}>{f}</option>)}
                </select>
              </div>
              <div>
                <label className="field-label">Intensity</label>
                <select className="select-input" value={intensity} onChange={e => setIntensity(e.target.value)}>
                  {["AI Decides","Light","Moderate","Hard","Max Effort"].map(i => <option key={i}>{i}</option>)}
                </select>
              </div>
              <div style={{ display: "flex", alignItems: "flex-end" }}>
                <div style={{ fontFamily: "var(--font-mono)", fontSize: 12, color: "var(--muted)", paddingBottom: 10 }}>
                  {availableExercises.length} exercises available
                </div>
              </div>
            </div>

            {garminCreds && (
              <div className="garmin-pill">
                {garminSyncing
                  ? <span className="gp-muted">⌚ Syncing Garmin…</span>
                  : garminData
                    ? <>⚡ BB: {garminData.bodyBattery ?? "—"} · HRV: {garminData.hrvStatus ?? "—"} · Sleep: {garminData.sleepScore ?? "—"}</>
                    : <span className="gp-muted">⌚ Garmin connected — sync pending</span>
                }
              </div>
            )}

            <button className="build-btn" onClick={handleGenerate} disabled={generating || availableExercises.length === 0}>
              {generating ? "Building..." : "Generate Workout"}
            </button>

            {error && <div className="error-msg">{error}</div>}
            {generating && <div className="loading-state"><span className="loading-dots">Building your session</span></div>}

            {generatedWorkout && <>
              <div className="workout-card">
                <div className="workout-card-header">
                  <div className="workout-title">{generatedWorkout.title}</div>
                  <div className="workout-meta">{duration}min{focus !== "AI Decides" ? ` · ${focus}` : ""}</div>
                </div>
                {generatedWorkout.exercises.map((ex, i) => {
                  const desc = EXERCISE_MAP.get(ex.name)?.description;
                  return (
                    <div key={i} className="workout-exercise">
                      <span className="ex-num">{String(i+1).padStart(2,"0")}</span>
                      <span className="ex-name" style={{ flex: 1 }}>{ex.name}</span>
                      <span className="ex-sets">{ex.sets}×{ex.reps}</span>
                      <span className="ex-tag">{ex.rest}</span>
                      <button className="swap-btn" onClick={() => setSwapIndex(i)}>swap</button>
                      {desc && <div className="ex-desc" style={{ flexBasis: "100%", paddingLeft: 28 }}>{desc}</div>}
                    </div>
                  );
                })}
                {generatedWorkout.notes && <div className="notes-block">💡 {generatedWorkout.notes}</div>}
              </div>

              <div className="refine-bar">
                <input
                  className="search-input"
                  style={{ flex: 1, marginBottom: 0 }}
                  placeholder="Ask for changes — 'fewer exercises', 'swap out legs', 'make it harder'"
                  value={refinementText}
                  onChange={e => setRefinementText(e.target.value)}
                  onKeyDown={e => e.key === "Enter" && !refining && handleRefine()}
                />
                <button className="refine-btn" onClick={handleRefine} disabled={refining || !refinementText.trim()}>
                  {refining ? "Refining…" : "Refine"}
                </button>
              </div>

              <button className="save-btn" onClick={handleSave}>Save to My Plan →</button>
            </>}
          </>}

          {tab === "plan" && <>
            <p className="section-label">Saved Workouts — {plans.length} session{plans.length !== 1 ? "s" : ""}</p>
            {plans.length === 0
              ? <div className="plan-empty">No saved workouts yet.<br />Build one in the Build tab.</div>
              : plans.map(plan => (
                <div key={plan.id} className="plan-card">
                  <div className="plan-card-header">
                    <div>
                      <div className="plan-card-title">{plan.title}</div>
                      <div className="plan-card-meta">{plan.meta} · {plan.savedAt}</div>
                    </div>
                    <button className="delete-btn" onClick={() => handleDelete(plan.id)}>remove</button>
                  </div>
                  <div className="plan-exercises">
                    {plan.exercises.map((ex, i) => (
                      <div key={i} className="plan-ex-row">
                        <span style={{ fontFamily: "var(--font-mono)", fontSize: 11, color: "var(--muted)", width: 20 }}>{i+1}</span>
                        <span style={{ flex: 1 }}>{ex.name}</span>
                        <span style={{ fontFamily: "var(--font-mono)", fontSize: 12, color: "var(--accent)" }}>{ex.sets}×{ex.reps}</span>
                      </div>
                    ))}
                  </div>
                  {plan.notes && <div className="notes-block">💡 {plan.notes}</div>}
                </div>
              ))
            }
          </>}

          {tab === "library" && <>
            <p className="section-label">Exercise Database — {EXERCISES.length} movements</p>
            <input className="search-input" placeholder="Search exercises..." value={search} onChange={e => setSearch(e.target.value)} />
            <div className="filter-row">
              {MUSCLE_GROUPS.map(m => (
                <button key={m} className={`filter-chip${muscleFilter === m ? " active" : ""}`} onClick={() => setMuscleFilter(m)}>{m}</button>
              ))}
            </div>
            <div className="filter-row">
              <button className={`filter-chip${!showAll ? " active" : ""}`} onClick={() => setShowAll(p => !p)}>
                {showAll ? "Available only" : "Show all"}
              </button>
              <span style={{ fontFamily: "var(--font-mono)", fontSize: 11, color: "var(--muted)", alignSelf: "center" }}>
                {locLabel?.icon} {locLabel?.label}
              </span>
            </div>
            <div className="exercise-grid">
              {EXERCISES
                .filter(e => (muscleFilter === "All" || e.muscle === muscleFilter) && (!search || e.name.toLowerCase().includes(search.toLowerCase())) && (showAll || availableEquipment.includes(e.equipment)))
                .map(ex => {
                  const avail = availableEquipment.includes(ex.equipment);
                  return (
                    <div key={ex.id} className="exercise-row" style={{ opacity: avail ? 1 : 0.4 }}>
                      <div className="muscle-dot" style={{ background: MUSCLE_COLOR[ex.muscle] }} />
                      <div style={{ flex: 1, display: "flex", flexDirection: "column", gap: 2 }}>
                        <div className="ex-name">{ex.name}</div>
                        {ex.description && <div className="ex-desc">{ex.description}</div>}
                      </div>
                      <span className="ex-tag">{ex.muscle}</span>
                      <span className="ex-tag">{ex.equipment}</span>
                      {!avail && <span className="ex-tag" style={{ color: "var(--accent2)", borderColor: "rgba(255,92,53,0.3)" }}>n/a</span>}
                    </div>
                  );
                })}
            </div>
          </>}

          {tab === "settings" && <>
            <div className="settings-section">
              <div className="settings-title">🏠 Home Gym</div>
              <div className="settings-desc">Check the equipment you have available. This filters what exercises the AI can use when you select Home Gym.</div>
              <div className="equip-grid">
                {ALL_EQUIPMENT.map(eq => {
                  const checked = homeGym.includes(eq);
                  return (
                    <div key={eq} className={`equip-row${checked ? " checked" : ""}`} onClick={() => toggleEquip(eq)}>
                      <div className="equip-check">{checked && <span className="check-mark">✓</span>}</div>
                      <div className="equip-name">{eq}</div>
                    </div>
                  );
                })}
              </div>
              <div style={{ display: "flex", alignItems: "center" }}>
                <button className="save-settings-btn" onClick={saveSettings}>Save</button>
                {settingsSaved && <span className="saved-flash">✓ Saved to this device</span>}
              </div>
            </div>

            <div className="settings-section">
              <div className="settings-title">🏋️ Full Gym</div>
              <div className="settings-desc">Assumes all equipment available. No configuration needed.</div>
              <div className="equip-grid">
                {ALL_EQUIPMENT.map(eq => (
                  <div key={eq} className="equip-row checked" style={{ cursor: "default" }}>
                    <div className="equip-check"><span className="check-mark">✓</span></div>
                    <div className="equip-name">{eq}</div>
                    <span className="locked-tag">always on</span>
                  </div>
                ))}
              </div>
            </div>

            <div className="settings-section">
              <div className="settings-title">✈️ Away</div>
              <div className="settings-desc">Bodyweight only — hotel room, park, anywhere.</div>
              <div className="equip-grid">
                <div className="equip-row checked" style={{ cursor: "default" }}>
                  <div className="equip-check"><span className="check-mark">✓</span></div>
                  <div className="equip-name">Bodyweight</div>
                  <span className="locked-tag">always on</span>
                </div>
              </div>
            </div>

            <div className="settings-section">
              <div className="settings-title">⌚ Garmin Connect</div>
              <div className="settings-desc">Connect your Garmin account so the AI can factor in Body Battery, HRV, sleep quality, and recent training load when generating workouts. Only stored on this device.</div>
              {garminCreds ? (
                <div>
                  <div className="equip-row checked" style={{ cursor: "default", marginBottom: 8 }}>
                    <div className="equip-check"><span className="check-mark">✓</span></div>
                    <div className="equip-name" style={{ fontSize: 13 }}>{garminCreds.email}</div>
                  </div>
                  {garminData && (
                    <div className="equip-summary" style={{ marginBottom: 8 }}>
                      <strong>Last sync</strong>
                      Body Battery: {garminData.bodyBattery ?? "—"} · HRV: {garminData.hrvStatus ?? "—"} · Sleep score: {garminData.sleepScore ?? "—"} · Strength sessions (7d): {garminData.recentSessions ?? "—"}
                    </div>
                  )}
                  <button className="disconnect-btn" onClick={disconnectGarmin}>Disconnect Garmin</button>
                </div>
              ) : (
                <div className="garmin-connect-form">
                  <input className="search-input" style={{ marginBottom: 0 }} placeholder="Garmin Connect email" value={garminEmail} onChange={e => setGarminEmail(e.target.value)} autoComplete="username" />
                  <input className="search-input" style={{ marginBottom: 0 }} type="password" placeholder="Garmin Connect password" value={garminPassword} onChange={e => setGarminPassword(e.target.value)} autoComplete="current-password" />
                  {garminConnectError && <div className="error-msg" style={{ marginBottom: 0 }}>{garminConnectError}</div>}
                  <button className="save-settings-btn" disabled={garminSyncing || !garminEmail || !garminPassword} onClick={connectGarmin}>
                    {garminSyncing ? "Connecting…" : "Connect Garmin"}
                  </button>
                </div>
              )}
            </div>
          </>}

        </main>

        {swapIndex !== null && (
          <div className="overlay" onClick={() => setSwapIndex(null)}>
            <div className="modal" onClick={e => e.stopPropagation()}>
              <div className="modal-header">
                <div className="modal-title">Swap — {generatedWorkout?.exercises[swapIndex]?.name}</div>
                <button className="close-btn" onClick={() => setSwapIndex(null)}>×</button>
              </div>
              <div className="modal-list">
                {swapCandidates.length === 0
                  ? <div style={{ padding: 24, textAlign: "center", fontFamily: "var(--font-mono)", fontSize: 13, color: "var(--muted)" }}>No alternatives available for this location.</div>
                  : swapCandidates.map(ex => (
                    <div key={ex.id} className="modal-ex" onClick={() => handleSwap(ex)}>
                      <div className="muscle-dot" style={{ background: MUSCLE_COLOR[ex.muscle] }} />
                      <div style={{ flex: 1, display: "flex", flexDirection: "column", gap: 2 }}>
                        <div style={{ fontWeight: 700 }}>{ex.name}</div>
                        {ex.description && <div className="ex-desc">{ex.description}</div>}
                      </div>
                      <span className="ex-tag">{ex.equipment}</span>
                    </div>
                  ))
                }
              </div>
            </div>
          </div>
        )}
      </div>
    </>
  );
}
